import { createAsyncThunk } from '@reduxjs/toolkit';
import EmailApi from '../../apis/email';
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import { resettingEmailLoader } from '../slices/email';
import { RootState } from '../../redux/store';
import { ComposeEmailParams, SendBulkEmailsParams } from '../../params/redux/email';

export const fetchUserMailsThunk = createAsyncThunk(
    'email/fetch-mails',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await EmailApi.fetchUserMails(
                state.app.token,
                client_id,
                location.pathname.includes('/app/leads') || location.pathname.includes('/lead-management') ? null : state.deal.selectedDeal?.id || null,
                !location.pathname.includes('/app/leads') && !location.pathname.includes('/lead-management') ? null : state.lead.selectedLead?.id || null
            );
            if (response?.success === 1) {
                return response?.thread;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingEmailLoader());
        }
    }
);

export const fetchGlobalThreadsThunk = createAsyncThunk(
    'email/global-threads',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await EmailApi.fetchGlobalThreads(state.app.token);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingEmailLoader());
        }
    }
);

export const fetchUserIntegrationThunk = createAsyncThunk(
    'email/fetch-user',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await EmailApi.fetchUserIntegration(state.app.token, client_id);
            if (response?.success === 1) {
                return response?.integrations;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingEmailLoader());
        }
    }
);

export const composeEmailThunk = createAsyncThunk(
    'email/compose',
    async (dataComing: ComposeEmailParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const data = {
                ...dataComing,
                client_id: dataComing.client_id || 0,
                deal_id: location.pathname.includes('/app/leads') || location.pathname.includes('/lead-management') ? null : state.deal.selectedDeal?.id || null,
                lead_id: !location.pathname.includes('/app/leads') && !location.pathname.includes('/lead-management') ? null : state.lead.selectedLead?.id || null
            };
            const response = await EmailApi.composeEmail(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchUserMailsThunk(dataComing.client_id || 0)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingEmailLoader());
        }
    }
);

export const fetchMailMessagesThunk = createAsyncThunk(
    'email/mail-messages',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await EmailApi.fetchMailMessages(
                state.app.token,
                state.email.selectedEmail?.id || 0,
                client_id
            );
            if (response?.success === 1) {
                return response?.thread;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingEmailLoader());
        }
    }
);

export const replyToThreadThunk = createAsyncThunk(
    'email/reply-to-thread',
    async (data: ComposeEmailParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await EmailApi.replyToThread(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchMailMessagesThunk(data.client_id || 0)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingEmailLoader());
        }
    }
);

export const markAllMailsAsReadThunk = createAsyncThunk(
    'email/mark-read',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await EmailApi.markAllMailsAsRead(state.app.token);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingEmailLoader());
        }
    }
);

export const deleteMailThunk = createAsyncThunk(
    'email/delete',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await EmailApi.deleteMail(state.app.token);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingEmailLoader());
        }
    }
);

export const fetchThreadMessagesThunk = createAsyncThunk(
    'email/thread-messages',
    async ({ thread_id, client_id } : {thread_id: number, client_id: number}, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await EmailApi.fetchThreadMessages(state.app.token, client_id, thread_id);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingEmailLoader());
        }
    }
);

export const sendBulkEmailsThunk = createAsyncThunk(
    'email/send-bulk',
    async (data: SendBulkEmailsParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await EmailApi.sendBulkEmails(state.app.token, data);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingEmailLoader());
        }
    }
);
